import { ArrowRight } from 'lucide-react';
import React from 'react';
import { useLocation } from 'react-router-dom';

import type { Post } from '@/api-client/posts';
import { PageHeader } from '@/components/layout/PageHeader';
import { CompactPostItem, LeadPostCard, PostGrid } from '@/components/post/PostCard';
import { ButtonLink } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

/** Articles per category block on the home page */
export const CATEGORY_BLOCK_SIZE = 4;
/** Articles in the "Legfrissebb" block: one lead story + a headline list */
export const LATEST_BLOCK_SIZE = 5;

/**
 * - `feature`: a big lead card with a headline list next to it (`reverse` swaps the sides)
 * - `grid`: a row of regular cards
 */
export type BlockLayout = 'feature' | 'grid';

interface PostBlockProps {
  posts: Post[];
  layout: BlockLayout;
  reverse?: boolean;
  /** The lead card of the top block is a bit bigger */
  leadSize?: 'lg' | 'md';
}

const PostBlock: React.FC<PostBlockProps> = ({ posts, layout, reverse = false, leadSize }) => {
  const location = useLocation();
  const linkState = { from: location.pathname };

  if (layout === 'grid') {
    // Three posts fill a 3-column row; a 4-column row would leave a hole
    return <PostGrid posts={posts} linkState={linkState} columns={posts.length === 3 ? 3 : 4} />;
  }

  const [lead, ...rest] = posts;
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <div
        className={`${rest.length > 0 ? 'lg:col-span-3' : 'lg:col-span-5'} ${reverse ? 'lg:order-2' : ''}`}
      >
        <LeadPostCard post={lead} linkState={linkState} size={leadSize} />
      </div>
      {rest.length > 0 && (
        <div className="lg:col-span-2 flex flex-col divide-y divide-neutral-800">
          {rest.map((post) => (
            <CompactPostItem key={post.id} post={post} linkState={linkState} />
          ))}
        </div>
      )}
    </div>
  );
};

const BlockSkeleton: React.FC<{ layout: BlockLayout }> = ({ layout }) =>
  layout === 'grid' ? (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: CATEGORY_BLOCK_SIZE }).map((_, i) => (
        <Skeleton key={i} className="h-80" />
      ))}
    </div>
  ) : (
    <div className="grid gap-6 lg:grid-cols-5">
      <Skeleton className="lg:col-span-3 min-h-[22rem] md:min-h-[28rem]" />
      <div className="lg:col-span-2 flex flex-col gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-20 w-28 md:h-24 md:w-36 shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton variant="text" width="50%" />
              <Skeleton variant="text" height="20px" />
              <Skeleton variant="text" height="20px" width="70%" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

interface HomeSectionProps {
  title: string;
  subtitle?: string;
  /** "See all" link of the block */
  moreLink: { to: string; label: string };
  posts: Post[];
  isPending: boolean;
  error: Error | null;
  layout: BlockLayout;
  reverse?: boolean;
  leadSize?: 'lg' | 'md';
}

/** A titled block of the home page. Renders nothing once loaded if it has no posts. */
export const HomeSection: React.FC<HomeSectionProps> = ({
  title,
  subtitle,
  moreLink,
  posts,
  isPending,
  error,
  layout,
  reverse,
  leadSize,
}) => {
  if (!isPending && posts.length === 0 && !error) return null;

  return (
    <section className="py-10 md:py-14" aria-label={title}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-6">
        <PageHeader
          level="section"
          title={title}
          subtitle={subtitle}
          action={
            <ButtonLink
              to={moreLink.to}
              variant="outline"
              size="sm"
              className="self-start md:self-auto"
            >
              {moreLink.label}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>
          }
        />
        {isPending ? (
          <BlockSkeleton layout={layout} />
        ) : posts.length === 0 ? (
          <p className="text-neon-red font-body">{error?.message}</p>
        ) : (
          <PostBlock posts={posts} layout={layout} reverse={reverse} leadSize={leadSize} />
        )}
      </div>
    </section>
  );
};

/** Home page block layout per category, alternating so the page doesn't repeat itself. */
export const categoryBlockLayout = (index: number): { layout: BlockLayout; reverse?: boolean } =>
  index % 2 === 1 ? { layout: 'grid' } : { layout: 'feature', reverse: index % 4 === 2 };
